// Every string for the Systems section. Split out of agencyCopy.js because
// this is the part of the site that grows — each new module lands here as one
// more entry, and the section layout is built to absorb them without a
// redesign.
//
// `status` is load-bearing: 'live' renders a running demo, 'build' renders a
// spec panel instead. Listing something as 'build' is how a module appears on
// the site before it ships, without pretending it already has.

export const services = {
  label: 'Systems',
  headline: 'A stack that keeps growing. Each module removes a job nobody should be doing.',
  intro:
    'None of these are packages. Every one is built around the tools you already run, the way your process actually works, and what you are willing to change. One runs in production today, one is being built on ourselves, and the rest we build to order.',
  // Sits directly under the section intro. The single most common wrong
  // assumption a visitor makes is that these are fixed products.
  customNote:
    'Nothing here ships as-is. Scope, integrations, and where a human stays in the loop are decided per build.',

  cards: [
    // ── Live ────────────────────────────────────────────────────────────
    {
      key: 'support',
      ref: 'SYS-01',
      status: 'live',
      title: 'Every question answered before anyone asks you',
      body:
        'An agent that has read your site, your products, and every ticket you have ever closed. It answers on the page in seconds and pulls in a human only when a human is actually needed.',
      outcome: 'Your inbox stops being the bottleneck.',
      bestFor: 'Online retail · clinics · schools · property',
      demo: 'chat',
      tools: ['Your website', 'WhatsApp', 'Zendesk / Intercom', 'Slack', 'Cal.com'],
      examples: [
        'Answer product, pricing, and availability questions 24/7',
        'Handle shipping status and return requests without a ticket',
        'Book appointments or property viewings via chat',
        'Qualify inbound leads before your sales team sees them',
        'Voice and text on the same page',
      ],
    },
    {
      key: 'ops',
      ref: 'SYS-02',
      status: 'offered',
      title: 'The work between your tools, done without anyone doing it',
      body:
        'Every place a person is being a human API — copying a field from one system into another, chasing a document, waiting on an approval — rebuilt as a workflow that runs itself and pings a person only at the decision.',
      outcome: 'Your team stops moving data and starts making calls.',
      bestFor: 'Any business where staff move data between systems all day',
      demo: 'workflow',
      tools: ['Google Workspace', 'Slack', 'HubSpot', 'QuickBooks / Xero', 'Notion'],
      examples: [
        'Client onboarding — project folder, contract draft, billing setup, welcome email, from one form',
        'Employee onboarding — provisions accounts across HR, email, Slack, VPN',
        'Invoice matching — 3-way match between PO, receipt, and vendor invoice',
        'Data enrichment — given a company, pulls website and registry data into the CRM',
        'Meeting summariser with action items delegated into your project tool',
      ],
    },
    {
      key: 'sales',
      ref: 'SYS-03',
      status: 'build',
      title: 'A pipeline that fills while your team is asleep',
      body:
        'Finding the right companies, researching each one properly, and writing an opening line that references something real. Your salespeople wake up to conversations, not a spreadsheet of names.',
      outcome: 'More pipeline without more headcount.',
      bestFor: 'B2B services · agencies · software companies',
      demo: 'pipeline',
      tools: ['Google Maps', 'Apify', 'Gmail / Outlook', 'HubSpot', 'Google Sheets'],
      examples: [
        'Finds target companies from criteria — industry, size, tech stack',
        'Enriches each prospect with role, seniority, recent news, funding',
        'Drafts personalised openers that reference something real',
        'Follows up on non-responders from a different angle',
        'Books the meeting once a prospect replies',
      ],
    },
    // ── Built to order ────────────────────────────────────
    {
      key: 'voice',
      ref: 'SYS-04',
      status: 'offered',
      title: 'Calls made and answered without a headset in the room',
      body:
        'A voice agent that runs the calls nobody wants to make twice — confirming appointments, chasing overdue invoices, qualifying an inbound lead before it reaches a person. It books straight into the calendar and hands off mid-call when the conversation earns a human.',
      outcome: 'Your phone line stops being a headcount problem.',
      bestFor: 'Clinics · property · collections · high-volume inbound',
      spec: {
        stage: 'Built to order',
        note: 'Scoped and priced per deployment. Your number, your calendar, your CRM.',
        rows: [
          { k: 'Telephony',   v: 'Twilio programmable voice' },
          { k: 'Speech',      v: 'ElevenLabs · streaming TTS + ASR' },
          { k: 'Latency',     v: 'Sub-second turn-taking target' },
          { k: 'Handoff',     v: 'Warm transfer to a human mid-call' },
          { k: 'Records to',  v: 'CRM + call transcript with action items' },
        ],
      },
      tools: ['Twilio', 'ElevenLabs', 'Cal.com', 'HubSpot', 'Your phone number'],
      examples: [
        'Confirm and reschedule appointments the day before',
        'Chase overdue invoices without a person making the call',
        'Qualify inbound leads before they reach your sales team',
        'Answer the line after hours instead of sending to voicemail',
        'Hand off to a human mid-call when it stops being routine',
      ],
    },
    {
      key: 'crm',
      ref: 'SYS-05',
      status: 'offered',
      title: 'One screen that tells you what is actually happening',
      body:
        'Most businesses have the numbers and cannot see them — spread across an ERP, a spreadsheet, a CRM, and somebody\'s inbox. This pulls them into one place that updates itself, and flags the thing that moved before you go looking for it.',
      outcome: 'You stop reconstructing last month to decide about next month.',
      bestFor: 'Anyone running the business off three spreadsheets and a hunch',
      spec: {
        stage: 'Built to order',
        note: 'Shaped around whatever you already run. No rip-and-replace.',
        rows: [
          { k: 'Ingest',      v: 'ERP · CRM · billing · sheets, on a schedule' },
          { k: 'Store',       v: 'Postgres warehouse, versioned models' },
          { k: 'Surface',     v: 'Embedded dashboards, no new login' },
          { k: 'Alerting',    v: 'Threshold + anomaly, to Slack or WhatsApp' },
          { k: 'Access',      v: 'Row-level, so each team sees only theirs' },
        ],
      },
      tools: ['Postgres', 'HubSpot', 'Metabase', 'Your ERP', 'Slack'],
      examples: [
        'One view across ERP, CRM, billing, and spreadsheets',
        'Numbers that refresh on a schedule instead of by hand',
        'Alerts when a metric moves, before someone notices in a meeting',
        'Row-level access so each team sees only their own',
        'Exports that finance will actually accept',
      ],
    },
    {
      key: 'mcp',
      ref: 'SYS-06',
      status: 'offered',
      title: 'The layer that lets AI touch your systems safely',
      body:
        'Every module above needs to reach into an ERP, a database, or an internal API — and the reason most AI projects stall is that nobody built that reach properly. Scoped permissions, an audit trail on every call, and a hard boundary on what a model is allowed to do. This is the plumbing the rest of the stack runs on.',
      outcome: 'AI gets access to your systems without getting the keys to them.',
      bestFor: 'Anyone whose real data lives behind an ERP or an internal API',
      spec: {
        stage: 'Built to order',
        note: 'The connector layer every other module sits on. Built per system, per permission set.',
        rows: [
          { k: 'Protocol',    v: 'MCP — Model Context Protocol servers' },
          { k: 'Connects',    v: 'SAP · Odoo · Postgres · internal REST' },
          { k: 'Permissions', v: 'Per-tool scopes, deny by default' },
          { k: 'Audit',       v: 'Every call logged with actor and payload' },
          { k: 'Boundary',    v: 'Writes gated behind explicit approval' },
        ],
      },
      tools: ['MCP', 'SAP / Odoo', 'Postgres', 'Internal APIs', 'Google Workspace'],
      examples: [
        'Give an agent read access to the ERP without giving it credentials',
        'Scope each tool so a model can never call what it should not',
        'Log every call with who asked, what ran, and what came back',
        'Gate every write behind an explicit human approval',
        'Reuse one connector across every module in the stack',
      ],
    },
  ],

  dogfood: {
    label: 'Note',
    body:
      'The agent on this page is ours, and it is the same software we deploy. The pipeline that found you is ours too — currently being built on ourselves before it goes anywhere near a client. Nothing gets sold here that we have not had to keep running at 3am.',
  },
};

/*
 * Demo scenarios.
 *
 * Each live module cycles through several scenarios rather than replaying one
 * loop — a single repeating clip reads as a canned graphic, several read as a
 * system handling whatever arrives. Kept as data so a scenario can be added
 * without touching the animation code.
 */
export const DEMO_SCENARIOS = {
  chat: [
    {
      label: 'Shipping question',
      chrome: 'front · live on your site',
      turns: [
        { side: 'visitor', text: 'Do you ship to Karachi?' },
        { side: 'agent',   text: 'Yes — 2–3 days to Karachi, PKR 250 flat. Want me to check stock on something?' },
      ],
      close: 'Resolved · 1.2s · no ticket created',
    },
    {
      label: 'Booking',
      chrome: 'front · live on your site',
      turns: [
        { side: 'visitor', text: 'Can I see the Gulberg unit this weekend?' },
        { side: 'agent',   text: 'Saturday 11am or 3pm are open. Which suits you?' },
        { side: 'visitor', text: '3pm works.' },
      ],
      close: 'Booked · added to calendar · agent notified',
    },
    {
      label: 'Handoff',
      chrome: 'front · live on your site',
      turns: [
        { side: 'visitor', text: 'I was charged twice for order 4471.' },
        { side: 'agent',   text: 'I can see both charges. This one needs a person — putting you through to Ayesha now.' },
      ],
      close: 'Escalated · 8s · context passed to human',
    },
  ],

  workflow: [
    {
      label: 'Client onboarding',
      chrome: 'workflow · client onboarding',
      trigger: 'New client form submitted',
      steps: [
        'Project folder created',
        'Contract drafted from template',
        'Billing profile set up',
        'Welcome email sent',
      ],
      close: '4 steps · 0 people · 11s',
    },
    {
      label: 'Invoice matching',
      chrome: 'workflow · 3-way invoice match',
      trigger: 'Vendor invoice received',
      steps: [
        'Purchase order located',
        'Goods receipt matched',
        'Line totals reconciled',
        'Flagged: PKR 4,200 variance',
      ],
      close: '1 exception raised · routed to finance',
    },
    {
      label: 'Employee onboarding',
      chrome: 'workflow · new starter',
      trigger: 'Offer accepted in HR',
      steps: [
        'Email and Slack provisioned',
        'VPN and repo access granted',
        'Payroll record created',
        'Day-one checklist sent to manager',
      ],
      close: '4 systems · 0 tickets · 40s',
    },
  ],

  pipeline: [
    {
      label: 'Logistics + clinics',
      chrome: 'pipeline · found + enriched + drafted',
      rows: [
        { co: 'Meezan Logistics', who: 'Ops Director', sig: 'hiring 3 ops roles' },
        { co: 'Zeta Clinics',     who: 'Founder',      sig: 'opened 2nd branch' },
        { co: 'Karachi Textiles', who: 'Head of Sales', sig: 'new ERP rollout' },
      ],
      draft: '“Saw you’re opening a second branch — how are you handling intake across both?”',
      close: 'Replied · meeting booked',
    },
    {
      label: 'Retail + education',
      chrome: 'pipeline · found + enriched + drafted',
      rows: [
        { co: 'Saeed & Sons',    who: 'Managing Dir.', sig: 'launched online store' },
        { co: 'Beaconhouse Nth', who: 'Admin Head',    sig: 'admissions season' },
        { co: 'Orient Traders',  who: 'COO',           sig: 'migrating to Odoo' },
      ],
      draft: '“You just moved to Odoo — who is handling the data that did not migrate cleanly?”',
      close: 'Replied · call Thursday',
    },
  ],
};
